import { atom } from 'jotai';
import { AuthState } from '../types/auth';
import { supabase } from './supabase';
import { parseDomain } from './utils/subdomain';
import { authLogger } from './logger';

// Initialize auth state atom
export const authStateAtom = atom<AuthState>({
  user: null,
  community: null,
  isLoading: true,
  error: null,
});

// Domain detection using path-based routing and subdomain
export const getCurrentDomain = () => {
  const pathname = window.location.pathname;
  const { type } = parseDomain();

  const isPlatform =
    // Check domain type first - this is the most reliable indicator
    type === 'platform' ||
    type === 'app' ||
    // Platform-prefixed routes (but only if not on community subdomain)
    (pathname.startsWith('/platform') && type !== 'community') ||
    // Platform subdomain
    window.location.host.startsWith('platform.');
  return { isPlatform };
};

// Get community slug from path or subdomain

export const getCommunitySlug = () => {
  try {
    const pathname = window.location.pathname;
    const { isPlatform } = getCurrentDomain();

    // Skip if platform
    if (isPlatform) return null;

    // Check path first (handles /community/slug routes)
    const pathMatch = pathname.match(/^\/community\/([^/]+)/);
    if (pathMatch) {
      return pathMatch[1];
    }

    // Check query params for member routes (handles /member?community=slug)
    const params = new URLSearchParams(window.location.search);
    const communityParam = params.get('community');
    if (communityParam) {
      return communityParam;
    }

    // Use subdomain utility which handles both development and production
    const { subdomain, type } = parseDomain();
    authLogger?.debug(
      { subdomain, type },
      'Using parseDomain for community slug'
    );

    // Only return if it's a community subdomain
    if (type === 'community') {
      return subdomain;
    }

    return null;
  } catch (error) {
    authLogger?.error({ error }, 'Error getting community slug');
    return null;
  }
};

// Initialize auth using Supabase's built-in session management
export const initializeAuth = async () => {
  try {
    const {
      data: { session },
      error,
    } = await supabase.auth.getSession();
    if (error) throw error;

    if (session?.user) {
      const { data: profile } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', session.user.id)
        .single();

      const slug = getCommunitySlug();
      if (slug) {
        const { data: community } = await supabase
          .from('communities')
          .select('*')
          .eq('slug', slug)
          .single();

        return { user: profile, community, isLoading: false, error: null };
      }

      return { user: profile, community: null, isLoading: false, error: null };
    }

    return { user: null, community: null, isLoading: false, error: null };
  } catch (error) {
    console.error('Auth initialization error:', error);
    return {
      user: null,
      community: null,
      isLoading: false,
      error: 'Failed to initialize auth',
    };
  }
};

// Use Supabase's built-in auth methods
export const signInWithPassword = async (email: string, password: string) => {
  const { isPlatform } = getCurrentDomain();
  if (!isPlatform) {
    throw new Error(
      'Password sign-in is only available on the platform domain'
    );
  }

  const { data, error } = await supabase.auth.signInWithPassword({
    email,
    password,
  });
  if (error) throw error;
  return data;
};

export const signInWithMagicLink = async (email: string) => {
  const { isPlatform } = getCurrentDomain();
  if (isPlatform) {
    throw new Error(
      'Magic link sign-in is only available on community domains'
    );
  }

  const slug = getCommunitySlug();
  if (!slug) throw new Error('Invalid community domain');

  const { data, error } = await supabase.auth.signInWithOtp({
    email,
    options: {
      data: { community_slug: slug },
    },
  });

  if (error) throw error;
  return data;
};

export const verifyOtp = async (email: string, token: string) => {
  const { data, error } = await supabase.auth.verifyOtp({
    email,
    token,
    type: 'email',
  });

  if (error) throw error;

  if (!data.session?.user) {
    throw new Error('No session created');
  }

  // Ensure profile exists
  const user = data.session.user;
  const { data: profile } = await supabase
    .from('profiles')
    .select('id')
    .eq('id', user.id)
    .single();

  if (!profile) {
    const userMetadata = user.user_metadata || {};
    await supabase.from('profiles').insert({
      id: user.id,
      email: user.email,
      first_name: userMetadata.given_name || userMetadata.first_name || '',
      last_name: userMetadata.family_name || userMetadata.last_name || '',
      full_name: userMetadata.name || userMetadata.full_name || '',
      avatar_url: userMetadata.picture || userMetadata.avatar_url || '',
      role: 'member',
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    });
  }

  // Ensure community membership
  const slug = getCommunitySlug();
  if (slug) {
    const { data: community } = await supabase
      .from('communities')
      .select('id, settings')
      .eq('slug', slug)
      .single();

    if (community) {
      const { data: membership } = await supabase
        .from('community_members')
        .select('profile_id')
        .eq('profile_id', user.id)
        .eq('community_id', community.id)
        .single();

      if (!membership) {
        const accessMode = community.settings?.access_mode;
        const memberStatus =
          accessMode === 'application_required' || accessMode === 'hybrid'
            ? 'pending'
            : 'active';

        await supabase.from('community_members').insert({
          profile_id: user.id,
          community_id: community.id,
          status: memberStatus,
          onboarding_completed: false,
          joined_at: new Date().toISOString(),
        });
      }
    }
  }

  return data;
};

// Use Supabase's signOut directly
export const signOut = () => supabase.auth.signOut();
